import React, {useState} from "react";
import {toast} from "react-toastify";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import InputText from "../../../components/Inputs/InputText";
import SelectFieldComponent, {SelectOptionInterface} from "../../../components/SelectFieldComponent";
import AdminTable, {AdminTableColumn} from "../components/AdminTable";
import ConfirmDialog from "../components/ConfirmDialog";
import {AdminFile} from "../../../interfaces/admin";
import {
    useGetAdminFilesQuery,
    useRetryAdminFileMutation,
    useDeleteAdminFileMutation,
} from "../../../storage/adminApi";
import {useGetUsersQuery} from "../../../storage/api";
import localStyles from "../styles.module.scss";

const AdminFiles = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sortBy, setSortBy] = useState("id");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [filterUserId, setFilterUserId] = useState("");
    const [filterDateFrom, setFilterDateFrom] = useState("");
    const [filterDateTo, setFilterDateTo] = useState("");
    const [filterBbStatus, setFilterBbStatus] = useState("");
    const [detailFile, setDetailFile] = useState<AdminFile | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<AdminFile | null>(null);

    const params: Record<string, any> = {page, per_page: pageSize, sort_by: sortBy, sort_order: sortOrder};
    if (filterUserId) params.user_id = filterUserId;
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;
    if (filterBbStatus) params.bb_status = filterBbStatus;

    const {data, isLoading} = useGetAdminFilesQuery(params);
    const {data: users} = useGetUsersQuery();
    const [retryFile] = useRetryAdminFileMutation();
    const [deleteFile] = useDeleteAdminFileMutation();

    const handleRetry = async (fileId: number) => {
        try {
            await retryFile(fileId).unwrap();
            toast.success("File retry queued");
        } catch (e) {
            toast.error("Failed to retry file");
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteFile(deleteTarget.id).unwrap();
            toast.success("File deleted");
            setDeleteTarget(null);
        } catch (e) {
            toast.error("Failed to delete file");
        }
    };

    const formatSize = (size: string) => {
        const bytes = parseInt(size, 10);
        if (isNaN(bytes)) return size;
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const userOptions: SelectOptionInterface[] = [
        {value: "", label: "All Users"},
        ...(users?.map((u) => ({value: String(u.id), label: u.username})) ?? []),
    ];

    const selectedUserOption = userOptions.find((o) => o.value === filterUserId) ?? null;

    const bbStatusOptions: SelectOptionInterface[] = [
        {value: "", label: "All"},
        {value: "uploaded", label: "Uploaded"},
        {value: "pending", label: "Pending"},
    ];

    const selectedBbOption = bbStatusOptions.find((o) => o.value === filterBbStatus) ?? null;

    const columns: AdminTableColumn[] = [
        {label: "ID", key: "id", sortable: true},
        {label: "User", key: "username", render: (value: string) => value ?? "-"},
        {
            label: "Original Name",
            key: "origin_name",
            render: (value: string) => (
                <span className={localStyles.textEllipsis}>{value}</span>
            ),
        },
        {label: "MIME", key: "mime_type"},
        {
            label: "Size",
            key: "size",
            render: (value: string) => formatSize(value),
        },
        {
            label: "BB Status",
            key: "bb_file_id",
            render: (value: string) =>
                value
                    ? <span className={`${localStyles.badge} ${localStyles.badgeSuccess}`}>Uploaded</span>
                    : <span className={`${localStyles.badge} ${localStyles.badgeWarning}`}>Pending</span>,
        },
        {label: "Created", key: "created_at", sortable: true},
        {
            label: "Actions",
            key: "actions",
            render: (_: any, row: AdminFile) => (
                <div className={localStyles.actionsCell}>
                    <Button
                        id={`view-file-${row.id}`}
                        size="small"
                        background="bg-outline"
                        text="View"
                        click={(e) => {
                            e.stopPropagation();
                            setDetailFile(row);
                        }}
                    />
                    {!row.bb_file_id && (
                        <Button
                            id={`retry-file-${row.id}`}
                            size="small"
                            background="bg-primary"
                            text="Retry"
                            click={(e) => {
                                e.stopPropagation();
                                handleRetry(row.id);
                            }}
                        />
                    )}
                    <Button
                        id={`delete-file-${row.id}`}
                        size="small"
                        background="bg-danger"
                        text="Delete"
                        click={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(row);
                        }}
                    />
                </div>
            ),
        },
    ];

    return (
        <div>
            <h4 className="fw-600" style={{marginBottom: 16}}>Files</h4>

            {/* Filter Bar */}
            <div className={localStyles.filterBar}>
                <div className={localStyles.filterGroup}>
                    <label>User</label>
                    <SelectFieldComponent
                        id="filter-user"
                        options={userOptions}
                        selectedValue={selectedUserOption}
                        placeholder="All Users"
                        handleSelect={(option: SelectOptionInterface) => {
                            setFilterUserId(option?.value ?? "");
                            setPage(1);
                        }}
                    />
                </div>
                <div className={localStyles.filterGroup}>
                    <label>From</label>
                    <InputText
                        id="filter-date-from"
                        type="date"
                        value={filterDateFrom}
                        handleChange={(value: string) => {
                            setFilterDateFrom(value);
                            setPage(1);
                        }}
                    />
                </div>
                <div className={localStyles.filterGroup}>
                    <label>To</label>
                    <InputText
                        id="filter-date-to"
                        type="date"
                        value={filterDateTo}
                        handleChange={(value: string) => {
                            setFilterDateTo(value);
                            setPage(1);
                        }}
                    />
                </div>
                <div className={localStyles.filterGroup}>
                    <label>BB Status</label>
                    <SelectFieldComponent
                        id="filter-bb-status"
                        options={bbStatusOptions}
                        selectedValue={selectedBbOption}
                        placeholder="All"
                        handleSelect={(option: SelectOptionInterface) => {
                            setFilterBbStatus(option?.value ?? "");
                            setPage(1);
                        }}
                    />
                </div>
            </div>

            <AdminTable
                columns={columns}
                data={data?.items ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                }}
                loading={isLoading}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={(newSortBy, newSortOrder) => {
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                    setPage(1);
                }}
            />

            {/* File Detail Modal */}
            {detailFile && (
                <Modal
                    show
                    handleClose={() => setDetailFile(null)}
                    width={600}
                    closeOut
                    element={
                        <div>
                            <div className={localStyles.modalTitle}>File #{detailFile.id}</div>
                            <div className={localStyles.modalBody}>
                                <div className={localStyles.detailField}>
                                    <strong>Original Name:</strong> {detailFile.origin_name ?? "-"}
                                </div>
                                <div className={localStyles.detailField}>
                                    <strong>File Name:</strong> {detailFile.file_name ?? "-"}
                                </div>
                                <div className={localStyles.detailField}>
                                    <strong>User:</strong> {detailFile.username ?? "-"} (ID: {detailFile.user_id})
                                </div>
                                <div className={localStyles.detailField}>
                                    <strong>Link ID:</strong> {detailFile.link_id}
                                </div>
                                <div className={localStyles.detailField}>
                                    <strong>MIME Type:</strong> {detailFile.mime_type ?? "-"}
                                </div>
                                <div className={localStyles.detailField}>
                                    <strong>Size:</strong> {formatSize(detailFile.size)}
                                </div>
                                <div className={localStyles.detailField}>
                                    <strong>File Path:</strong>{" "}
                                    <span className={localStyles.textMuted}>{detailFile.file_path ?? "-"}</span>
                                </div>

                                <div className={localStyles.sectionTitle}>BackBlaze</div>
                                <div className={localStyles.detailField}>
                                    <strong>Status:</strong>{" "}
                                    {detailFile.bb_file_id
                                        ? <span className={`${localStyles.badge} ${localStyles.badgeSuccess}`}>Uploaded</span>
                                        : <span className={`${localStyles.badge} ${localStyles.badgeWarning}`}>Pending</span>
                                    }
                                </div>
                                {detailFile.bb_file_id && (
                                    <>
                                        <div className={localStyles.detailField}>
                                            <strong>BB File ID:</strong> {detailFile.bb_file_id}
                                        </div>
                                        <div className={localStyles.detailField}>
                                            <strong>BB File Name:</strong> {detailFile.bb_file_name ?? "-"}
                                        </div>
                                        <div className={localStyles.detailField}>
                                            <strong>BB Link:</strong>{" "}
                                            {detailFile.bb_link_to_file ? (
                                                <a
                                                    href={detailFile.bb_link_to_file}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{wordBreak: "break-all"}}
                                                >
                                                    {detailFile.bb_link_to_file}
                                                </a>
                                            ) : "-"}
                                        </div>
                                    </>
                                )}

                                <div className={localStyles.sectionTitle}>Timestamps</div>
                                <div className={localStyles.detailField}>
                                    <strong>Created:</strong> {detailFile.created_at ?? "-"}
                                </div>
                                <div className={localStyles.detailField}>
                                    <strong>Updated:</strong> {detailFile.updated_at ?? "-"}
                                </div>
                            </div>

                            <div className={localStyles.modalFooter}>
                                {!detailFile.bb_file_id && (
                                    <Button
                                        id="detail-retry"
                                        size="small"
                                        background="bg-primary"
                                        text="Retry Upload"
                                        click={() => {
                                            handleRetry(detailFile.id);
                                            setDetailFile(null);
                                        }}
                                    />
                                )}
                                <Button
                                    id="detail-close"
                                    size="small"
                                    background="bg-outline-grey"
                                    text="Close"
                                    click={() => setDetailFile(null)}
                                />
                            </div>
                        </div>
                    }
                />
            )}

            <ConfirmDialog
                show={!!deleteTarget}
                title="Delete File"
                message={`Are you sure you want to delete file "${deleteTarget?.origin_name}"?`}
                onConfirm={handleDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default <AdminFiles/>;
